import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Users, Calendar, ShieldOff, ShieldCheck, Fingerprint, Loader2, ChevronDown, Trash2, Pencil, X, Save, Printer, Send, KeyRound, UserPlus, Upload, Tag, CheckSquare, Square, Globe2 } from 'lucide-react';
import Barcode from '../components/Barcode';
import AdminLayout from '../components/AdminLayout';
import CustomerPriceOverridesPanel from '../components/CustomerPriceOverridesPanel';
import BulkCustomerUpload from '../components/BulkCustomerUpload';
import ExportCustomersPanel from '../components/ExportCustomersPanel';
import { customerDisplayLabels, customerPrimaryLabel } from '../lib/customerName';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatDateTime } from '../lib/dateFormat';

const formatDate = (iso) => (iso ? formatDateTime(iso) : '—');

const AdminCustomers = () => {
  const { authHeaders } = useAdminAuth();
  const { settings } = useSettings();
  const customerCardCfg = settings?.card_settings?.customer_card || {};
  const cardBarH = Number(customerCardCfg.barcode_height_mm) || 14;     // mm
  const cardBarW = Number(customerCardCfg.barcode_width_mm)  || 0.45;   // mm
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  // Feb 2026 — split the page into two tabs: local customers (default,
  // storefront + admin-onboarded) and export customers (persistent
  // master registry of international buyers, keyed by EXP-XXXXXX).
  const [tab, setTab] = useState('local');
  const [expanded, setExpanded] = useState(null);
  const [passkeys, setPasskeys] = useState({}); // email → []
  const [busy, setBusy] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editing, setEditing] = useState(null); // customer doc being edited
  const [savingEdit, setSavingEdit] = useState(false);

  // "Add customer" manual onboarding (admin skips OTP verification).
  const [adding, setAdding] = useState(false);
  const emptyDraft = { name: '', email: '', phone: '', business_name: '', address: '' };
  const [draft, setDraft] = useState(emptyDraft);
  const [creating, setCreating] = useState(false);
  // "Bulk upload" modal — imports the customer master from an ERP-style
  // .xlsx/.csv keyed by 6-digit `Cust` code.
  const [bulkOpen, setBulkOpen] = useState(false);

  const createCustomer = async () => {
    const name = (draft.name || '').trim();
    const email = (draft.email || '').trim().toLowerCase();
    if (!name) { toast({ title: 'Name is required' }); return; }
    if (!email || !email.includes('@')) { toast({ title: 'Valid email is required' }); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers`, {
        name,
        email,
        phone: (draft.phone || '').trim(),
        business_name: (draft.business_name || '').trim(),
        address: (draft.address || '').trim(),
      }, { headers: authHeaders });
      toast({ title: 'Customer added', description: `Customer number ${data.customer_number || ''}` });
      setAdding(false);
      setDraft(emptyDraft);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to add customer';
      toast({ title: 'Failed', description: detail });
    } finally {
      setCreating(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customers`, { headers: authHeaders });
      setCustomers(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/pricing/tiers`).then(({ data }) => setTiers(data)).catch(() => setTiers([]));
    axios.get(`${API}/categories`).then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCustomerTier = async (email, slug) => {
    setBusy(`tier:${email}`);
    try {
      const { data } = await axios.put(
        `${API}/admin/customers/${encodeURIComponent(email)}/tier`,
        { pricing_tier: slug },
        { headers: authHeaders },
      );
      setCustomers((list) =>
        list.map((c) => (c.email === email ? { ...c, pricing_tier: data.pricing_tier } : c)),
      );
      toast({ title: `Set ${email} to ${data.label}`, description: `${data.discount_pct}% trade discount` });
    } catch (e) {
      toast({ title: 'Could not update tier', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(null);
    }
  };

  const loadPasskeys = async (email) => {
    try {
      const { data } = await axios.get(`${API}/admin/customers/${encodeURIComponent(email)}/passkeys`, { headers: authHeaders });
      setPasskeys((p) => ({ ...p, [email]: data }));
    } catch {
      setPasskeys((p) => ({ ...p, [email]: [] }));
    }
  };

  const toggleExpand = (c) => {
    const next = expanded === c.email ? null : c.email;
    setExpanded(next);
    if (next) loadPasskeys(next);
  };

  const revokePasskey = async (email, id) => {
    if (!window.confirm('Revoke this passkey? The customer will need to re-register it.')) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/customers/${encodeURIComponent(email)}/passkeys/${id}`, { headers: authHeaders });
      toast({ title: 'Passkey revoked' });
      await loadPasskeys(email);
    } catch (err) {
      toast({ title: 'Revoke failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(null);
    }
  };

  const revokeAllPasskeys = async (email) => {
    if (!window.confirm(`Revoke ALL passkeys for ${email}?`)) return;
    setBusy(`all-${email}`);
    try {
      const { data } = await axios.delete(`${API}/admin/customers/${encodeURIComponent(email)}/passkeys`, { headers: authHeaders });
      toast({ title: `Revoked ${data.deleted} passkey(s)` });
      await loadPasskeys(email);
    } catch {
      toast({ title: 'Revoke all failed' });
    } finally {
      setBusy(null);
    }
  };

  const printBarcodeCard = (c) => {
    if (!c?.customer_number) return;
    const cn = c.customer_number;
    const svg = document.querySelector(`[data-testid="edit-customer-barcode-card"] svg`)?.outerHTML;
    if (!svg) return;

    const html = `<!doctype html><html><head><title>Customer card — ${cn}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif; margin: 32px; color: #0F172A; }
        .brand { font-size: 12px; color: #0078D4; letter-spacing: 2px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
        h1 { font-size: 24px; margin: 0 0 4px; }
        .card { max-width: 420px; padding: 24px; border: 1px solid #E2E8F0; border-radius: 24px; margin-top: 16px; }
        .who { font-size: 14px; color: #475569; margin-bottom: 4px; }
        .name { font-size: 20px; font-weight: 700; color: #0F172A; margin-bottom: 18px; }
        .barcode { display: flex; justify-content: center; padding: 8px 0 4px; }
        .barcode svg { width: 100%; height: auto; max-width: 360px; }
        .num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 22px; letter-spacing: 4px; text-align: center; color: #0F172A; margin-top: 4px; font-weight: 700; }
        .hint { font-size: 12px; color: #64748B; margin-top: 16px; line-height: 1.55; }
        .hint b { color: #0F172A; }
        @media print { body { margin: 12px; } .card { border: none; padding: 0; } }
      </style></head><body>
      <div class="brand">Mohammed Riaz &amp; Partner LLC</div>
      <h1>Customer card</h1>
      <div class="card">
        ${c.business_name ? `<div class="who">${c.business_name}</div>` : ''}
        <div class="name">${(c.name || '').replace(/</g, '&lt;')}</div>
        <div class="barcode">${svg}</div>
        <div class="num">${cn}</div>
        <div class="hint">
          <b>How to use:</b> at checkout in the e-store, pick <b>Existing customer</b> then either
          enter the 6-digit number or tap the green <b>Scan</b> button and hold this card under the camera.
          Your details will be filled in automatically.
        </div>
      </div>
      </body></html>`;

    // Re-use an existing hidden iframe if present, else create one
    let iframe = document.getElementById('mr-print-frame');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'mr-print-frame';
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);
    }
    const idoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!idoc) {
      toast({ title: 'Print failed', description: 'Could not open the print preview.' });
      return;
    }
    idoc.open();
    idoc.write(html);
    idoc.close();
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        toast({ title: 'Print failed', description: err?.message || '' });
      }
    }, 150);
  };

  const emailBarcodeCard = async (c) => {
    if (!c?.email) return;
    if (!c?.customer_number || c.customer_number.length !== 6) {
      toast({ title: 'No customer number yet', description: 'Issue a 6-digit number before emailing the card.' });
      return;
    }
    if (!window.confirm(`Email customer card to ${c.email}?\n\nSender: MRP Online · Attachment: MRP-Customer-Card-${c.customer_number}.pdf`)) return;
    setBusy(`email-card-${c.email}`);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/${encodeURIComponent(c.email)}/email-card`,
        {},
        { headers: authHeaders },
      );
      toast({ title: 'Card emailed', description: `Sent to ${data.to} via ${data.provider || 'email'}` });
    } catch (e) {
      toast({ title: 'Email failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {
        name: editing.name || '',
        phone: editing.phone || '',
        business_name: editing.business_name || '',
        customer_number: editing.customer_number || '',
        address: editing.address || '',
        lat: editing.lat ?? null,
        lng: editing.lng ?? null,
        pricing_tier: editing.pricing_tier || 'retail',
        category: editing.category || '',
      };
      const { data } = await axios.put(
        `${API}/admin/customers/${encodeURIComponent(editing.email)}`,
        payload,
        { headers: authHeaders },
      );
      setCustomers((list) => list.map((c) => (c.email === editing.email ? { ...c, ...data } : c)));
      toast({ title: 'Customer updated' });
      setEditing(null);
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '' });
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteCustomer = async (c) => {
    const ok = window.confirm(
      `Permanently delete ${c.email}?\n\nThis removes the profile, passkeys, push subscriptions and in-app notifications.\nExisting orders are kept for accounting.`,
    );
    if (!ok) return;
    setBusy(`del-${c.email}`);
    try {
      await axios.delete(`${API}/admin/customers/${encodeURIComponent(c.email)}`, { headers: authHeaders });
      setCustomers((arr) => arr.filter((x) => x.email !== c.email));
      toast({ title: 'Customer deleted', description: c.email });
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(null);
    }
  };


  // Force-relogin: customers authenticate via email/SMS OTP (no password to
  // reset). This bumps `must_relogin_after` on the profile so any existing
  // JWT they hold instantly becomes invalid — the next request 401s, the
  // customer must re-verify via OTP. Used when an account looks compromised.
  const forceRelogin = async (c) => {
    if (!window.confirm(`Force ${c.email} to re-login?\nAll active sessions for this customer will be revoked immediately. They'll have to verify a fresh OTP on next sign-in.`)) return;
    setBusy(`relog-${c.email}`);
    try {
      await axios.post(`${API}/admin/customers/${encodeURIComponent(c.email)}/force-relogin`, {}, { headers: authHeaders });
      toast({ title: 'Sessions revoked', description: `${c.email} must re-verify OTP to sign in again.` });
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusy(null);
    }
  };

  const toggleDisable = async (c) => {
    const lock = !c.disabled;
    let reason = '';
    if (lock) {
      reason = window.prompt('Reason for locking this account? (shown internally only)') || '';
    }
    if (lock && reason === null) return;
    setBusy(`lock-${c.email}`);
    try {
      await axios.post(
        `${API}/admin/customers/${encodeURIComponent(c.email)}/disable`,
        { disabled: lock, reason },
        { headers: authHeaders },
      );
      toast({
        title: lock ? 'Account locked' : 'Account unlocked',
        description: lock ? 'Customer cannot sign in until you unlock.' : '',
      });
      setCustomers((arr) => arr.map((x) => (x.email === c.email ? { ...x, disabled: lock, disabled_reason: reason } : x)));
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(
    () =>
      customers.filter((c) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          c.email.toLowerCase().includes(s) ||
          (c.name || '').toLowerCase().includes(s) ||
          (c.address || '').toLowerCase().includes(s)
        );
      }),
    [customers, q],
  );

  const categoryLabel = (slug) => {
    if (!slug) return '';
    const hit = categories.find((c) => c.slug === slug);
    return hit?.name || slug;
  };

  const toggleSelect = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.email));
  const someFilteredSelected = filtered.some((c) => selected.has(c.email));

  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filtered.forEach((c) => next.delete(c.email));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((c) => next.add(c.email));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const applyBulkCategory = async (mode /* 'assign' | 'clear' */) => {
    const emails = Array.from(selected);
    if (emails.length === 0) {
      toast({ title: 'No customers selected' });
      return;
    }
    const slug = mode === 'clear' ? '' : (bulkCategory || '').trim();
    if (mode === 'assign' && !slug) {
      toast({ title: 'Pick a division first' });
      return;
    }
    const label = slug ? categoryLabel(slug) : 'no category';
    if (!window.confirm(
      `Assign ${label} to ${emails.length} customer(s)?\n\nExisting category on these customers will be ${mode === 'clear' ? 'cleared' : 'replaced'}.`,
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/bulk-edit-category`,
        { emails, category: slug },
        { headers: authHeaders },
      );
      setCustomers((list) => list.map((c) => (selected.has(c.email) ? { ...c, category: slug } : c)));
      toast({
        title: mode === 'clear' ? 'Category cleared' : 'Category assigned',
        description: `${data.updated} of ${data.matched || emails.length} customer(s) updated${slug ? ` → ${label}` : ''}`,
      });
      clearSelection();
    } catch (err) {
      toast({ title: 'Bulk update failed', description: err?.response?.data?.detail || err?.message || '' });
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">Customers</h1>
            <p className="text-neutral-500 mt-1">
              Signed-in customers with their location, passkeys, and account controls.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setBulkOpen(true)}
              variant="outline"
              className="border-[#0078D4] text-[#0078D4] hover:bg-[#DEECF9]"
              data-testid="customer-bulk-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Bulk upload
            </Button>
            <Button
              onClick={() => { setDraft(emptyDraft); setAdding(true); }}
              className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
              data-testid="add-customer-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add customer
            </Button>
          </div>
        </div>

        <BulkCustomerUpload
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          authHeaders={authHeaders}
          onUploaded={() => load()}
        />

        {/* Feb 2026 — tab switcher between local vs. export customers. */}
        <div className="mb-5 inline-flex rounded-full bg-neutral-100 p-1 gap-1" data-testid="customers-tab-switcher">
          <button
            type="button"
            onClick={() => setTab('local')}
            className={`px-4 py-1.5 text-sm font-semibold rounded-full transition inline-flex items-center gap-1.5 ${
              tab === 'local' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'
            }`}
            data-testid="customers-tab-local"
          >
            <Users className="w-3.5 h-3.5" /> Local customers
          </button>
          <button
            type="button"
            onClick={() => setTab('export')}
            className={`px-4 py-1.5 text-sm font-semibold rounded-full transition inline-flex items-center gap-1.5 ${
              tab === 'export' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'
            }`}
            data-testid="customers-tab-export"
          >
            <Globe2 className="w-3.5 h-3.5" /> Export customers
          </button>
        </div>

        {tab === 'export' ? (
          <ExportCustomersPanel API={API} authHeaders={authHeaders} />
        ) : (
        <>
        {adding && (
          <div className="mb-6 bg-white rounded-2xl border border-neutral-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Add a new customer</h2>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="text-neutral-400 hover:text-neutral-700"
                aria-label="Close"
                data-testid="add-customer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-neutral-500 mb-4">
              Adding from here skips the customer&#39;s mobile OTP step — the phone is trusted on creation, and a random 6-digit customer number is auto-assigned.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Business name <span className="font-normal text-neutral-400">(optional)</span></label>
                <Input value={draft.business_name} onChange={(e) => setDraft((d) => ({ ...d, business_name: e.target.value }))} placeholder="ABC Trading LLC" className="mt-1" data-testid="add-customer-business-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Name</label>
                <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Full name" className="mt-1" data-testid="add-customer-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Email</label>
                <Input value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} placeholder="name@example.com" className="mt-1" data-testid="add-customer-email" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Mobile</label>
                <Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="+96891234567" className="mt-1" data-testid="add-customer-phone" />
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Address</label>
                <Input value={draft.address} onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))} placeholder="Street, area, city" className="mt-1" data-testid="add-customer-address" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdding(false)} disabled={creating}>Cancel</Button>
              <Button onClick={createCustomer} disabled={creating} className="bg-[#0078D4] hover:bg-[#005A9E]" data-testid="add-customer-save">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create customer'}
              </Button>
            </div>
          </div>
        )}

        <div className="max-w-md mb-4">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, address…"
            className="h-10"
            data-testid="customer-search"
          />
        </div>

        {/* Bulk-edit category toolbar */}
        <div
          className={`mb-4 rounded-2xl border p-3 flex flex-wrap items-center gap-3 transition ${
            selected.size > 0
              ? 'border-[#0078D4] bg-[#DEECF9]/60 shadow-sm'
              : 'border-neutral-200 bg-white'
          }`}
          data-testid="customer-bulk-toolbar"
        >
          <button
            type="button"
            onClick={toggleSelectAllFiltered}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-[#0078D4]"
            data-testid="customer-bulk-select-all"
            disabled={filtered.length === 0}
            title={allFilteredSelected ? 'Deselect all filtered' : 'Select all filtered'}
          >
            {allFilteredSelected ? (
              <CheckSquare className="w-4 h-4 text-[#0078D4]" />
            ) : someFilteredSelected ? (
              <CheckSquare className="w-4 h-4 text-[#0078D4]/60" />
            ) : (
              <Square className="w-4 h-4 text-neutral-400" />
            )}
            {allFilteredSelected ? 'Deselect all' : 'Select all filtered'}
          </button>
          <div className="text-xs text-neutral-600" data-testid="customer-bulk-count">
            <b>{selected.size}</b> selected
            {filtered.length ? <span className="text-neutral-400"> · {filtered.length} in view</span> : null}
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Tag className="w-4 h-4 text-neutral-500" />
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              disabled={bulkBusy || selected.size === 0}
              className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30 disabled:opacity-50"
              data-testid="customer-bulk-category-select"
            >
              <option value="">— Select division —</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <Button
              onClick={() => applyBulkCategory('assign')}
              disabled={bulkBusy || selected.size === 0 || !bulkCategory}
              className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white h-9"
              data-testid="customer-bulk-assign-btn"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Tag className="w-4 h-4 mr-1" />}
              Assign
            </Button>
            <Button
              variant="outline"
              onClick={() => applyBulkCategory('clear')}
              disabled={bulkBusy || selected.size === 0}
              className="rounded-full border-red-300 text-red-600 hover:bg-red-50 h-9"
              data-testid="customer-bulk-clear-btn"
              title="Clear the division on selected customers"
            >
              Clear division
            </Button>
            {selected.size > 0 && (
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-neutral-500 hover:text-neutral-800 underline"
                data-testid="customer-bulk-deselect"
              >
                Deselect all
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-neutral-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <div className="inline-flex w-14 h-14 rounded-full bg-[#DEECF9] items-center justify-center mb-4">
              <Users className="w-6 h-6 text-[#0078D4]" />
            </div>
            <h3 className="text-lg font-bold text-[#0F172A]">No customers yet</h3>
            <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
              When customers sign in via OTP and allow location access, they'll appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((c) => {
              const isOpen = expanded === c.email;
              const list = passkeys[c.email] || [];
              return (
                <div
                  key={c.email}
                  className={`bg-white rounded-2xl border transition ${
                    c.disabled ? 'border-red-200 bg-red-50/30' : selected.has(c.email) ? 'border-[#0078D4]' : 'border-neutral-200'
                  }`}
                  data-testid={`customer-row-${c.email}`}
                >
                  <div className="w-full flex items-center gap-3 p-4">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(c.email); }}
                      className="flex-shrink-0 p-1 -m-1 rounded hover:bg-neutral-100"
                      aria-label={selected.has(c.email) ? 'Deselect' : 'Select'}
                      data-testid={`customer-select-${c.email}`}
                    >
                      {selected.has(c.email) ? (
                        <CheckSquare className="w-5 h-5 text-[#0078D4]" />
                      ) : (
                        <Square className="w-5 h-5 text-neutral-400" />
                      )}
                    </button>
                    <button
                      onClick={() => toggleExpand(c)}
                      className="flex-1 flex items-center gap-4 text-left min-w-0"
                    >
                    <div className="flex-1 min-w-0">
                      {/* Feb 2026 — Row header restructured to expose
                          Business Name + Contact Person as the two
                          primary columns (matching the Export
                          customer table). Business is the top line
                          (falls back to Contact when unset — the
                          storefront OTP signup path doesn't always
                          collect a business_name). Contact person is
                          a labelled sub-line below. */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-[#0F172A]" data-testid={`customer-business-${c.email}`}>
                          {customerPrimaryLabel(c)}
                        </div>
                        {c.customer_number && (
                          <span
                            className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#DEECF9] text-[#0078D4] font-mono"
                            data-testid={`customer-number-${c.email}`}
                            title="Customer number — share this with the customer so they can reuse it at checkout."
                          >
                            #{c.customer_number}
                          </span>
                        )}
                        {c.disabled && (
                          <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Locked
                          </span>
                        )}
                        {c.pricing_tier && c.pricing_tier !== 'retail' && (
                          <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            {c.pricing_tier}
                          </span>
                        )}
                        {c.category && (
                          <span
                            className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                            data-testid={`customer-category-${c.email}`}
                            title={`Category: ${categoryLabel(c.category)}`}
                          >
                            <Tag className="w-3 h-3" /> {categoryLabel(c.category)}
                          </span>
                        )}
                      </div>
                      {/* Contact person line — only when the business
                          name is distinct from the contact person
                          (case-insensitive). Suppresses the duplicate
                          when both fields hold the same value. */}
                      {(() => {
                        const labels = customerDisplayLabels(c);
                        return labels.secondary ? (
                          <div
                            className="text-xs text-neutral-600 mt-0.5"
                            data-testid={`customer-contact-${c.email}`}
                          >
                            Contact: <span className="font-semibold">{labels.secondary}</span>
                          </div>
                        ) : null;
                      })()}
                      <div className="text-xs text-neutral-500 inline-flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3" /> {c.email}
                      </div>
                    </div>
                    <div className="hidden md:block text-xs text-neutral-500 max-w-[260px] truncate">
                      {c.address || (c.lat ? `${c.lat.toFixed(3)}, ${c.lng.toFixed(3)}` : '—')}
                    </div>
                    <div className="hidden lg:block text-xs text-neutral-500">{formatDate(c.last_seen_at)}</div>
                    <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-5 pt-1 border-t border-neutral-100 space-y-4">
                      {/* B2B trade tier */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-xl bg-emerald-50/60 border border-emerald-100">
                        <div className="flex-1">
                          <div className="text-[10px] font-bold tracking-widest uppercase text-emerald-700">B2B trade tier</div>
                          <div className="text-xs text-emerald-900/80 mt-0.5">
                            Discount applies to every product price for this customer's cart and checkout.
                          </div>
                        </div>
                        <select
                          value={c.pricing_tier || 'retail'}
                          onChange={(e) => setCustomerTier(c.email, e.target.value)}
                          disabled={busy === `tier:${c.email}` || tiers.length === 0}
                          className="h-9 rounded-lg border border-emerald-200 bg-white px-3 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-400/30 disabled:opacity-50"
                          data-testid={`customer-tier-${c.email}`}
                        >
                          {(tiers.length ? tiers : [{ slug: 'retail', label: 'Retail', discount_pct: 0 }]).map((t) => (
                            <option key={t.slug} value={t.slug}>
                              {t.label} ({t.discount_pct}%)
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-xs">
                        {c.customer_number && <Info label="Customer number"><span className="font-mono tracking-wider">{c.customer_number}</span></Info>}
                        {/* Feb 2026 — Always render Business name +
                            Contact person in the detail view so ops
                            sees both fields on every customer, not
                            only when a business_name is present.
                            Emphasis matches the Export customer
                            layout (Business first, Contact second). */}
                        <Info label="Business name" testid={`customer-detail-business-${c.email}`}>
                          {c.business_name || <span className="text-neutral-400 italic">Not set</span>}
                        </Info>
                        <Info label="Contact person" testid={`customer-detail-contact-${c.email}`}>
                          {c.name || <span className="text-neutral-400 italic">Not set</span>}
                        </Info>
                        {c.phone && <Info label="Phone"><a href={`tel:${c.phone}`} className="text-[#0078D4] hover:underline">{c.phone}</a></Info>}
                        <Info label="Signed up">{formatDate(c.signed_up_at)}</Info>
                        <Info label="Last seen">{formatDate(c.last_seen_at)}</Info>
                        {c.address && <Info label="Address">{c.address}</Info>}
                        {c.lat && (
                          <Info label="Location">
                            <a
                              href={`https://www.google.com/maps?q=${c.lat},${c.lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[#0078D4] hover:underline inline-flex items-center gap-1"
                            >
                              <MapPin className="w-3 h-3" /> {c.lat.toFixed(5)}, {c.lng.toFixed(5)}
                            </a>
                          </Info>
                        )}
                        {c.disabled_reason && (
                          <Info label="Lock reason"><span className="text-red-700">{c.disabled_reason}</span></Info>
                        )}
                      </div>

                      {/* Passkeys */}
                      <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-700">
                            <Fingerprint className="w-3.5 h-3.5" /> Passkeys ({list.length})
                          </div>
                          {list.length > 0 && (
                            <button
                              onClick={() => revokeAllPasskeys(c.email)}
                              disabled={busy === `all-${c.email}`}
                              className="text-xs font-semibold text-red-600 hover:bg-red-100 px-2.5 py-1 rounded-full"
                              data-testid={`revoke-all-passkeys-${c.email}`}
                            >
                              Revoke all
                            </button>
                          )}
                        </div>
                        {list.length === 0 ? (
                          <p className="text-xs text-neutral-500">No passkeys registered. Customer signs in via OTP only.</p>
                        ) : (
                          <ul className="space-y-2">
                            {list.map((pk) => (
                              <li
                                key={pk.id}
                                className="flex items-center justify-between bg-white rounded-lg border border-neutral-200 px-3 py-2 text-xs"
                                data-testid={`passkey-${pk.id}`}
                              >
                                <div>
                                  <div className="font-semibold text-[#0F172A]">{pk.label || pk.device_name || 'Device passkey'}</div>
                                  <div className="text-neutral-500 mt-0.5">
                                    Added {formatDate(pk.created_at)}
                                    {pk.last_used && ` · last used ${formatDate(pk.last_used)}`}
                                  </div>
                                </div>
                                <button
                                  onClick={() => revokePasskey(c.email, pk.id)}
                                  disabled={busy === pk.id}
                                  className="text-red-600 hover:bg-red-50 p-2 rounded-full"
                                  data-testid={`revoke-passkey-${pk.id}`}
                                  title="Revoke"
                                >
                                  {busy === pk.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Account control */}
                      <div className="flex justify-end gap-2 flex-wrap">
                        <Button
                          onClick={() => setEditing({ ...c })}
                          variant="outline"
                          className="rounded-full"
                          data-testid={`edit-customer-${c.email}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Edit profile
                        </Button>
                        <Button
                          onClick={() => toggleDisable(c)}
                          disabled={busy === `lock-${c.email}`}
                          variant={c.disabled ? 'default' : 'outline'}
                          className={
                            c.disabled
                              ? 'rounded-full bg-emerald-500 hover:bg-emerald-600 text-white'
                              : 'rounded-full border-red-300 text-red-600 hover:bg-red-50'
                          }
                          data-testid={`toggle-lock-${c.email}`}
                        >
                          {busy === `lock-${c.email}` ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : c.disabled ? (
                            <ShieldCheck className="w-4 h-4 mr-1" />
                          ) : (
                            <ShieldOff className="w-4 h-4 mr-1" />
                          )}
                          {c.disabled ? 'Unlock account' : 'Lock account'}
                        </Button>
                        <Button
                          onClick={() => forceRelogin(c)}
                          disabled={busy === `relog-${c.email}`}
                          variant="outline"
                          className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`force-relogin-${c.email}`}
                          title="Customers don't have passwords (OTP-only). This invalidates all their active sessions — they'll have to re-verify via OTP."
                        >
                          {busy === `relog-${c.email}` ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <KeyRound className="w-4 h-4 mr-1" />
                          )}
                          Force re-login
                        </Button>
                        <Button
                          onClick={() => deleteCustomer(c)}
                          disabled={busy === `del-${c.email}`}
                          variant="outline"
                          className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`delete-customer-${c.email}`}
                        >
                          {busy === `del-${c.email}` ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-1" />
                          )}
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !savingEdit && setEditing(null)}
          data-testid="edit-customer-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#0F172A]">Edit customer</h2>
                <p className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {editing.email}
                </p>
              </div>
              <button
                onClick={() => !savingEdit && setEditing(null)}
                className="text-neutral-400 hover:text-neutral-700"
                data-testid="edit-customer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { k: 'name', label: 'Full name', placeholder: 'Ahmed Al-Riyami' },
                { k: 'phone', label: 'Phone', placeholder: '99000000', type: 'tel' },
                { k: 'business_name', label: 'Business name', placeholder: 'ABC Trading LLC' },
                { k: 'address', label: 'Default delivery address', placeholder: 'Street, building, area' },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">{f.label}</label>
                  <Input
                    type={f.type || 'text'}
                    value={editing[f.k] || ''}
                    onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-1.5 h-11 rounded-xl"
                    data-testid={`edit-customer-${f.k}`}
                  />
                </div>
              ))}

              <div>
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                  Customer code
                </label>
                <Input
                  maxLength={50}
                  value={editing.customer_number || ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      // Feb 2026 — customer code accepts alphanumeric,
                      // spaces and hyphens up to 50 chars. Strip everything
                      // else on input (prevents pasting stray chars).
                      customer_number: e.target.value.replace(/[^A-Za-z0-9 \-]/g, '').slice(0, 50),
                    })
                  }
                  placeholder="Auto-issued on first order — edit only if needed"
                  className="mt-1.5 h-11 rounded-xl font-mono tracking-wider"
                  data-testid="edit-customer-customer_number"
                />
                <p className="text-[11px] text-neutral-500 mt-1">
                  Issued automatically when the customer places their first order (a random 6-digit number). Edit to override — accepts alphanumeric, spaces and hyphens up to 50 characters, must be unique.
                </p>

                {/* Barcode preview + print. Only shown for the classic
                    6-digit numeric codes because the printed barcode
                    label + scanner target that exact format. Non-numeric
                    codes still work everywhere else. */}
                {editing.customer_number && /^\d{6}$/.test(editing.customer_number) && (
                  <div className="mt-3 rounded-xl border border-neutral-200 bg-white p-3 space-y-3" data-testid="edit-customer-barcode-card">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <Barcode
                          value={editing.customer_number}
                          width={Math.max(0.8, cardBarW * 3)}
                          height={Math.max(20, Math.round(cardBarH * 3.6))}
                          fontSize={11}
                          displayValue={false}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Customer card</div>
                        <div className="text-xs text-neutral-700 mt-0.5">
                          Print or email the card — customer scans it at checkout to skip the form.
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => printBarcodeCard(editing)}
                        className="flex-1 rounded-full h-10"
                        data-testid="edit-customer-print-barcode"
                      >
                        <Printer className="w-4 h-4 mr-1.5" /> Print
                      </Button>
                      <Button
                        type="button"
                        onClick={() => emailBarcodeCard(editing)}
                        disabled={busy === `email-card-${editing.email}`}
                        className="flex-1 rounded-full h-10 bg-[#0078D4] hover:bg-[#005A9E] text-white"
                        data-testid="edit-customer-email-barcode"
                        title={`Email card to ${editing.email}`}
                      >
                        {busy === `email-card-${editing.email}` ? (
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-1.5" />
                        )}
                        {busy === `email-card-${editing.email}` ? 'Sending…' : 'Email card'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Lat/Lng — pair, with a tooltip */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">Latitude</label>
                  <Input
                    type="number"
                    step="any"
                    value={editing.lat ?? ''}
                    onChange={(e) => setEditing({ ...editing, lat: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="23.5880"
                    className="mt-1.5 h-11 rounded-xl font-mono"
                    data-testid="edit-customer-lat"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">Longitude</label>
                  <Input
                    type="number"
                    step="any"
                    value={editing.lng ?? ''}
                    onChange={(e) => setEditing({ ...editing, lng: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="58.4080"
                    className="mt-1.5 h-11 rounded-xl font-mono"
                    data-testid="edit-customer-lng"
                  />
                </div>
              </div>
              {editing.lat && editing.lng && (
                <a
                  href={`https://www.google.com/maps?q=${editing.lat},${editing.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#0078D4] hover:underline inline-flex items-center gap-1 -mt-2"
                >
                  <MapPin className="w-3 h-3" /> Preview on Google Maps
                </a>
              )}

              <div>
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">B2B pricing tier</label>
                <select
                  value={editing.pricing_tier || 'retail'}
                  onChange={(e) => setEditing({ ...editing, pricing_tier: e.target.value })}
                  className="mt-1.5 w-full h-11 rounded-xl border border-neutral-300 px-3 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30"
                  data-testid="edit-customer-tier"
                >
                  {(tiers.length ? tiers : [{ slug: 'retail', label: 'Retail', discount_pct: 0 }]).map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.label} — {t.discount_pct}% discount
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">
                  Discount is applied automatically to every product price in this customer's cart and at checkout.
                </p>
              </div>

              <div>
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">Division</label>
                <select
                  value={editing.category || ''}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="mt-1.5 w-full h-11 rounded-xl border border-neutral-300 px-3 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30"
                  data-testid="edit-customer-category"
                >
                  <option value="">— No division —</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">
                  Group this customer under a product division for reporting and per-division campaigns.
                </p>
              </div>

              <div className="border-t border-neutral-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">Manual prices (per product)</label>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">
                  Override the tier discount with a flat negotiated price for specific SKUs.
                  When set, the manual price wins — the tier discount is ignored on that product.
                </p>
                <CustomerPriceOverridesPanel
                  email={editing.email}
                  discountPct={Number(
                    (tiers.find((t) => t.slug === (editing.pricing_tier || 'retail')) || {}).discount_pct || 0,
                  )}
                  api={API}
                  authHeaders={authHeaders}
                />
              </div>
            </div>

            <div className="p-5 border-t border-neutral-100 flex items-center justify-end gap-2">
              <Button
                onClick={() => setEditing(null)}
                disabled={savingEdit}
                variant="ghost"
                className="rounded-full"
                data-testid="edit-customer-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEdit}
                disabled={savingEdit}
                className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
                data-testid="edit-customer-save"
              >
                {savingEdit ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Info = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-0.5">{label}</div>
    <div>{children}</div>
  </div>
);

export default AdminCustomers;
